import { message } from "ant-design-vue";
// @ts-expect-error: Unreachable code error
// import TIMProfanityFilterPlugin from "tim-profanity-filter-plugin";
import TIMUploadPlugin from "tim-upload-plugin";
import { reactive } from "vue";
import apiIndex from "~/api";
import LibGenerateTestUserSig from "~/assets/js/tim/lib-generate-test-usersig-es.min.js";
import { useLinkUpload } from "~/composables/global/useLinkUpload";
import { TIM_APP_ID, TIM_SECRET_KEY } from "~/env";
import Common from "~/utils/common";
// 尝试直接导入
import TencentCloudChat from "@tencentcloud/chat";

type Message = any;
type MESSAGE_OPTIONS = any;
type ApiIndex = ReturnType<typeof apiIndex>;

// 延迟初始化chat，只在客户端环境中创建
let chat: any = null;

const getChat = () => {
  if (typeof window === "undefined") {
    return null; // 服务端环境返回null
  }

  if (!chat) {
    const options = {
      SDKAppID: +TIM_APP_ID,
    };
    chat = TencentCloudChat.create(options);
    chat.setLogLevel(1);

    // 注册腾讯云即时通信 IM 上传插件
    chat.registerPlugin({ "tim-upload-plugin": TIMUploadPlugin });

    // 注册腾讯云即时通信 IM 本地审核插件
    // chat.registerPlugin({
    //   "tim-profanity-filter-plugin": TIMProfanityFilterPlugin,
    // });

    // 设置事件监听
    setupEvents(chat);
  }

  return chat;
};

// 设置事件监听
const setupEvents = (chatInstance: any) => {
  chatInstance.on(TencentCloudChat.EVENT.SDK_READY, () => {
    setTimeout(() => {
      state.ready = true;
    }, 300);
  });

  //用户被踢下线
  chatInstance.on(TencentCloudChat.EVENT.KICKED_OUT, () => {
    message.warn("您的账号已在其他地方登录，您已被迫下线,如需继续使用请刷新页面", 5);
  });

  //网络状态发生改变
  // chatInstance.on(TencentCloudChat.EVENT.NET_STATE_CHANGE, () => {
  //   message.warn("网络状态发生改变");
  // });
};

const state = reactive({
  init: false, //是否已经初始化
  initLoading: false,
  ready: false, //SDK是否已经准备好
  /**
   * 未读留言数量
   */
  unreadCommentCount: 0,
  /**
   * 未读消息数量
   */
  unreadCount: 0,
  /**
   * 会话列表
   */
  conversationList: [] as any[],
  /**
   * 当前会话用户ID
   */
  currentUserId: "",
});

// 事件监听已移到 setupEvents 函数中

/**
 * 等待SDK加载完成
 */
function onReady(): Promise<void> {
  return new Promise((resolve) => {
    if (state.ready) {
      resolve();
      return;
    }
    requestAnimationFrame(() => {
      resolve(onReady());
    });
  });
}

function genUserSig(options: any) {
  const EXPIRETIME = 7 * 24 * 60 * 60; //签名过期时间
  const { SDKAppID, secretKey, userID } = options;
  const generator = new LibGenerateTestUserSig(+SDKAppID, secretKey, EXPIRETIME);
  const userSig = generator.genTestUserSig(userID);
  return userSig;
}

export class IndexClient {
  $api: ApiIndex;
  state = state;

  get chat() {
    return getChat();
  }

  constructor(api: ApiIndex) {
    this.$api = api;
    // 延迟设置事件监听，确保在客户端环境中
    if (typeof window !== "undefined") {
      this.setupEventListeners();
    }
  }

  private setupEventListeners() {
    const chatInstance = this.chat;
    if (!chatInstance) return;

    //监听会话未读总数变更
    chatInstance.on(TencentCloudChat.EVENT.TOTAL_UNREAD_MESSAGE_COUNT_UPDATED, ({ data }: any) => {
      this.loadUnreadCount(data);
    });
    //监听会话列表更新事件
    chatInstance.on(TencentCloudChat.EVENT.CONVERSATION_LIST_UPDATED, ({ data }: any) => {
      this.loadConversationList(data);
    });
  }

  /**
   * 登录tim
   */
  private async login(userID: any) {
    const userSig = genUserSig({
      SDKAppID: TIM_APP_ID,
      secretKey: TIM_SECRET_KEY,
      userID,
    });
    try {
      const { data } = await this.chat.login({ userID, userSig });
      return data;
    } catch (e) {
      console.warn("login error:", e);
      return Promise.reject(e);
    }
  }

  /**
   * 获取未读消息数量
   */
  private async loadUnreadCount(count?: number) {
    if (count) {
      state.unreadCount = count;
      return state.unreadCount;
    }
    const value = await this.chat.getTotalUnreadMessageCount();
    state.unreadCount = value;
    return state.unreadCount;
  }

  /**
   * 加载会话列表
   */
  private async loadConversationList(value?: any) {
    await onReady();
    if (value) {
      state.conversationList = value;
      return;
    }
    const { data } = await this.chat.getConversationList();
    state.conversationList = data.conversationList;
    return state.conversationList;
  }

  async init() {
    if (state.init) return;
    state.init = true;
    state.initLoading = true;
    const userId = await this.$api.ai.registerTIM();
    await this.login(userId);
    await onReady();
    await this.loadUnreadCount();
    await this.loadConversationList();
    state.initLoading = false;
  }

  /**
   * 根据UserId获取会话
   */
  getSessionByUserId(userId: string) {
    return state.conversationList.find((item: any) => item.userProfile.userID === userId);
  }

  /**
   * 拉取消息列表
   */
  async getMessageList(conversationID: string, nextReqMessageID?: string) {
    await onReady();
    const { data } = await this.chat.getMessageList({
      conversationID,
      nextReqMessageID,
    });
    return data;
  }

  /**
   * 将某会话下的未读消息状态设置为已读
   */
  async setMessageRead(conversationID: string) {
    await onReady();
    await this.chat.setMessageRead({ conversationID });
    this.loadUnreadCount();
  }

  /**
   * 发送消息
   */
  async sendMessage(message: Message) {
    await onReady();
    const { data } = await this.chat.sendMessage(message);
    return data;
  }

  /**
   * 创建文本消息
   */
  createTextMessage(options: MESSAGE_OPTIONS) {
    return this.chat.createTextMessage(options);
  }

  /**
   * 创建文件消息
   */
  async createFileMessage(options: any) {
    const { payload, to, conversationType, onProgress } = options;
    const { file } = payload;
    const suffix = Common.getFileSuffix(file.name);
    const imageSuffixList = ["jpg", "png", "jpeg", "gif", "bmp", "image", "webp"];

    let customData = null;
    if (imageSuffixList.includes(suffix)) {
      const formData = new FormData();
      formData.append("file", file);
      const { url, simpleUrl } = await this.$api.ai.uploadFileIm(formData, onProgress);
      const img = await Common.getImageInfoByFile(file);
      //图片消息
      customData = JSON.stringify({
        messageForShow: "[图片]",
        fileType: "image",
        fileName: file.name,
        fileSize: file.size,
        fileUrl: url,
        thumbFileUrl: simpleUrl,
        width: img.width,
        height: img.height,
        uuid: file.uid,
      });
    } else {
      const { createFileMd5, upload } = useLinkUpload();
      const { fileMd5 } = await createFileMd5(file);
      const getUploadParams = () =>
        this.$api.ai.fileUploadTempUrl({
          fileHash: fileMd5,
          fileName: file.name,
        });
      const { fileId, tempUrl } = await upload(file, getUploadParams);
      //文件消息
      customData = JSON.stringify({
        messageForShow: "[文件]",
        fileType: "file",
        fileName: file.name,
        fileSize: file.size,
        fileUrl: tempUrl,
        uuid: file.uid,
        fileId,
      });
    }
    return this.chat.createCustomMessage({
      to,
      conversationType,
      payload: { data: customData },
    });
  }
}

export default defineNuxtPlugin(() => {
  const $api = useApi();
  const tim = new IndexClient($api);
  return {
    provide: {
      tim,
    },
  };
});
